import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, Archive, Bot, CheckCircle2, Clock, Globe, LayoutTemplate, LoaderCircle, Plus, Rocket, Server, Terminal, Wand2, X,
  Copy, Check, Cpu, HardDrive, Box, Radio, Layers, ArrowUpRight
} from 'lucide-react';
import { generateRandomSuffix, validateSubdomainPrefix } from '../../utils/subdomainHelper';

function formatDeployDuration(ms) {
  const totalSec = Math.max(0, Math.floor(Number(ms) / 1000) || 0);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/**
 * AI Auto-Deploy: upload a ZIP + configuration → open workspace immediately,
 * then show final details only after Workspace AI verifies the app.
 */
export default function AIDeploy({
  onStatusMessage,
  onBack,
  api,
  beginDeploymentTracking,
  setDeploymentSessionId,
  openComposeWorkspace,
  closeComposeWorkspace,
  refreshDeploymentSession,
  setCurrentSessionProgress,
  deploymentSessionId,
  workspaceMinimized,
  restoreWorkspace,
  onTabChange
}) {
  const [appName, setAppName] = useState('');
  const [appNameError, setAppNameError] = useState('');
  const [platformSuffix] = useState(() => generateRandomSuffix());
  const [subdomainPrefix, setSubdomainPrefix] = useState('');
  const [subdomainError, setSubdomainError] = useState('');
  const [zipFile, setZipFile] = useState(null);
  const [domain, setDomain] = useState('');
  const [domainError, setDomainError] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(false);

  const handleCopyUrl = (urlToCopy) => {
    if (!urlToCopy) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(urlToCopy).then(() => {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      }).catch(() => fallbackCopy(urlToCopy));
    } else {
      fallbackCopy(urlToCopy);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (e) {}
  };

  const handleSubdomainChange = (e) => {
    const val = e.target.value;
    setSubdomainPrefix(val);
    const res = validateSubdomainPrefix(val);
    setSubdomainError(res.valid ? '' : res.error);
  };

  const validateDomain = (val) => {
    if (!val || !val.trim()) {
      setDomainError('');
      return true;
    }
    if (/devorants\.com$/i.test(val.trim())) {
      setDomainError('devorants.com subdomains cannot be used as custom domains.');
      return false;
    }
    setDomainError('');
    return true;
  };

  const handleDomainChange = (e) => {
    const val = e.target.value;
    setDomain(val);
    validateDomain(val);
  };
  const [internalPort, setInternalPort] = useState('3000');
  const [image, setImage] = useState('alma-node');
  const [cpus, setCpus] = useState('1.0');
  const [memory, setMemory] = useState('1024m');
  const [envText, setEnvText] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState('form'); // form | deploying | success | error
  const [detectedApps, setDetectedApps] = useState([]);
  const [selectedAppPath, setSelectedAppPath] = useState('');
  const [analyzingZip, setAnalyzingZip] = useState(false);
  const [deployResult, setDeployResult] = useState(null);
  const [deployError, setDeployError] = useState('');
  const [deployElapsedMs, setDeployElapsedMs] = useState(0);
  const [deployDurationMs, setDeployDurationMs] = useState(null);
  const [workspaceVerified, setWorkspaceVerified] = useState(false);
  // Multi-entry point management for detected apps
  const [entryPoints, setEntryPoints] = useState([]);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  const deployStartedAtRef = useRef(null);
  const deployResultRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    const stillRunning = phase === 'deploying' && Boolean(deployStartedAtRef.current) && !workspaceVerified && deployDurationMs == null;
    if (!stillRunning) return undefined;
    const tick = () => setDeployElapsedMs(Date.now() - deployStartedAtRef.current);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [phase, workspaceVerified, deployDurationMs]);

  const parseEnvText = (rawText) => {
    const env = {};
    String(rawText || '').split('\n').forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) return;
      const eqIdx = trimmed.indexOf('=');
      if (eqIdx > 0) {
        const k = trimmed.substring(0, eqIdx).trim();
        const v = trimmed.substring(eqIdx + 1).trim();
        if (k) env[k] = v;
      }
    });
    return env;
  };

  const sanitizeAppName = (name) =>
    String(name || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');

  const validateAppName = (val) => {
    if (!val) {
      setAppNameError('');
      return false;
    }
    if (/\s/.test(val)) {
      setAppNameError('Spaces are not allowed. Use hyphens (-) instead.');
      return false;
    }
    if (/[A-Z]/.test(val)) {
      setAppNameError('Uppercase letters are not allowed. Use lowercase only.');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(val)) {
      setAppNameError('Only lowercase letters, numbers, and hyphens (-) are allowed.');
      return false;
    }
    setAppNameError('');
    return true;
  };

  const handleAppNameChange = (e) => {
    const val = e.target.value;
    setAppName(val);
    validateAppName(val);
  };

  const normalizeResult = (payload = {}, fallback = {}) => {
    const result = payload.result || payload;
    const resolvedDomain = result.domain || fallback.domain || domain || '';
    const url = result.url || fallback.url || (resolvedDomain ? `http://${resolvedDomain}/` : '');
    return {
      ...result,
      domain: resolvedDomain,
      url,
      port: result.port != null ? result.port : fallback.port,
      internalPort: result.internalPort || fallback.internalPort || internalPort,
      cpus: result.cpus || fallback.cpus || cpus,
      memory: result.memory || fallback.memory || memory,
      image: result.image || fallback.image || image,
      containerName: result.containerName || result.runnerContainer || (result.ssh && result.ssh.containerId) || null,
      // Keep AI workspace verification apps/bindings for Admin / User / Backend links.
      apps: result.apps || payload.apps || fallback.apps || [],
      bindings: result.bindings || payload.bindings || fallback.bindings || [],
      publicUrls: result.publicUrls || payload.publicUrls || fallback.publicUrls || [],
      verification: result.verification || payload.verification || fallback.verification || null
    };
  };

  const buildWorkspaceInfo = (name, result = {}, extras = {}) => {
    const info = normalizeResult(result);
    return {
      isDeploying: false,
      deploymentType: 'backend-workspace',
      basePath: '/home/app',
      autoDeploy: false,
      initialTab: 'ai',
      domain: info.domain,
      url: info.url,
      port: info.port || null,
      containerId: info.containerName || null,
      containerName: info.containerName || null,
      isSSR: Boolean(info.isSSR),
      internalPort: info.internalPort,
      cpus: info.cpus,
      memory: info.memory,
      image: info.image,
      deployStartedAt: deployStartedAtRef.current,
      ...extras
    };
  };

  const finishFromWorkspace = (payload = {}) => {
    if (!mountedRef.current) return;
    const finishedAt = Date.now();
    const duration = finishedAt - (deployStartedAtRef.current || finishedAt);
    setDeployDurationMs(duration);
    setDeployElapsedMs(duration);
    setLoading(false);
    const prior = deployResultRef.current || {};
    const info = normalizeResult({
      ...prior,
      ...(payload.result || {}),
      ...payload,
      verification: payload.verification || prior.verification || null
    }, {
      cpus,
      memory,
      image,
      internalPort,
      domain
    });
    deployResultRef.current = info;
    setDeployResult(info);
    setWorkspaceVerified(true);
    setPhase('success');
    onStatusMessage('Deployment verified — details below', 'success');
  };

  const handOffToWorkspace = (name, result = {}) => {
    if (typeof openComposeWorkspace !== 'function') return;
    const info = normalizeResult(result, { cpus, memory, image, internalPort, domain });
    deployResultRef.current = info;
    setDeployResult(info);
    openComposeWorkspace(name, buildWorkspaceInfo(name, info, {
      isDeploying: false,
      autoDeploy: true,
      onAiVerified: (payload) => finishFromWorkspace(payload)
    }));
  };

  const pollUntilDone = (deploymentId, name) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (!mountedRef.current) return;
      const session = typeof refreshDeploymentSession === 'function'
        ? await refreshDeploymentSession(deploymentId)
        : await api.getDeploymentStatus(deploymentId).catch(() => null);
      if (!session) return;

      if (session.status === 'completed') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        handOffToWorkspace(name, session.result || session);
      } else if (session.status === 'failed') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        const errMsg = session.error || session.message || 'Workspace setup failed';
        const finishedAt = Date.now();
        setDeployDurationMs(finishedAt - (deployStartedAtRef.current || finishedAt));
        setDeployError(typeof errMsg === 'object' ? (errMsg.message || 'Workspace setup failed') : String(errMsg));
        setPhase('error');
        setLoading(false);
        onStatusMessage(`Deployment failed: ${typeof errMsg === 'object' ? errMsg.message : errMsg}`, 'error');
      }
    }, 1500);
  };

  const acceptZip = async (file) => {
    if (file && /\.zip$/i.test(file.name)) {
      setZipFile(file);
      setDetectedApps([]);
      setSelectedAppPath('');
      setEntryPoints([]);
      setUploadPct(0);
      if (typeof api.analyzeZip === 'function') {
        setAnalyzingZip(true);
        try {
          const res = await api.analyzeZip(file, (pct) => setUploadPct(pct));
          if (res.apps && res.apps.length > 0) {
            setDetectedApps(res.apps);
            setSelectedAppPath(res.apps[0].path);
            // Convert detected apps to entry points table
            const mappedEps = res.apps.map(app => ({
              path: app.path || './',
              role: app.type || app.role || 'service',
              port: app.port || 3000,
              domain: app.domain || app.subdomain || ''
            }));
            
            const uniqueEps = [];
            const seenPorts = new Set();
            for (const ep of mappedEps) {
              if (ep.port && !seenPorts.has(ep.port)) {
                seenPorts.add(ep.port);
                uniqueEps.push(ep);
              }
            }
            setEntryPoints(uniqueEps);
          } else {
            // Fallback if AI/logic couldn't detect anything
            setDetectedApps([{ path: './', role: 'service', port: 3000 }]);
            setSelectedAppPath('./');
            setEntryPoints([{ path: './', role: 'service', port: 3000, domain: '' }]);
            onStatusMessage('Could not automatically detect frameworks. Please configure the entry point manually.', 'info');
          }
        } catch (e) {
          onStatusMessage('Could not analyze ZIP contents: ' + e.message, 'warning');
        } finally {
          setAnalyzingZip(false);
        }
      }
    }
  };

  const handleCancelDeployment = async () => {
    try {
      if (deploymentSessionId && typeof api.cancelDeployment === 'function') {
        await api.cancelDeployment(deploymentSessionId);
      }
      if (pollRef.current) clearInterval(pollRef.current);
      setPhase('idle');
      setLoading(false);
      setAnalyzingZip(false);
      setUploadPct(0);
      onStatusMessage('Deployment cancelled', 'info');
      if (typeof closeComposeWorkspace === 'function') {
        closeComposeWorkspace({ cancelled: true });
      }
    } catch (err) {
      onStatusMessage(`Failed to cancel: ${err.message}`, 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!appName || !validateAppName(appName) || !zipFile) {
      onStatusMessage(appNameError || 'Please provide a valid application name and a ZIP file', 'error');
      return;
    }

    if (subdomainPrefix && !validateSubdomainPrefix(subdomainPrefix).valid) {
      onStatusMessage(subdomainError || 'Invalid subdomain format.', 'error');
      return;
    }

    if (domain && !validateDomain(domain)) {
      onStatusMessage(domainError || 'devorants.com subdomains cannot be used as custom domains.', 'error');
      return;
    }
    const safeName = sanitizeAppName(appName);
    const resolvedPrefix = (subdomainPrefix && subdomainPrefix.trim())
      ? subdomainPrefix.trim().toLowerCase()
      : safeName;
    const autoPlatformDomain = `${resolvedPrefix}-${platformSuffix}.devorants.com`;
    const finalDomain = (domain && domain.trim()) ? domain.trim() : autoPlatformDomain;

    if (typeof api.deployBackendZip !== 'function') {
      onStatusMessage('Backend ZIP deploy API is not available', 'error');
      return;
    }
    if (typeof openComposeWorkspace !== 'function') {
      onStatusMessage('Workspace UI is unavailable', 'error');
      return;
    }

    setLoading(true);
    setUploadPct(0);
    setDeployError('');
    setDeployResult(null);
    deployResultRef.current = null;
    setDeployDurationMs(null);
    setWorkspaceVerified(false);
    const started = Date.now();
    deployStartedAtRef.current = started;
    setDeployElapsedMs(0);
    setPhase('deploying');

    const deploymentId = beginDeploymentTracking('backend-workspace', safeName);
    if (typeof setDeploymentSessionId === 'function') setDeploymentSessionId(deploymentId);

    // Open workspace immediately so the user can see the progress
    if (typeof openComposeWorkspace === 'function') {
      openComposeWorkspace(safeName, {
        isDeploying: true,
        deploymentType: 'backend-workspace',
        basePath: selectedAppPath ? (selectedAppPath === '/' ? '/home/app' : `/home/app${selectedAppPath}`) : '/home/app',
        autoDeploy: true,
        initialTab: 'ai',
        internalPort: internalPort ? parseInt(internalPort, 10) : 3000,
        cpus,
        memory,
        image,
        domain: finalDomain,
        deployStartedAt: started,
        onAiVerified: (payload) => finishFromWorkspace(payload)
      });
    }

    try {
      // Filter valid entry points (must have a port)
      const validEntryPoints = entryPoints.filter(ep => ep.port && Number(ep.port) > 0);

      const response = await api.deployBackendZip(safeName, zipFile, {
        domain: finalDomain,
        port: internalPort ? parseInt(internalPort, 10) : 3000,
        image,
        cpus,
        memory,
        env: parseEnvText(envText),
        deploymentId,
        entryPoints: validEntryPoints.length > 0 ? validEntryPoints : undefined,
        onUploadProgress: (pct) => {
          setUploadPct(pct);
          if (typeof setCurrentSessionProgress === 'function') setCurrentSessionProgress(pct);
        }
      });

      if (!mountedRef.current) return;

      const pollId = response?.deploymentId || deploymentId;
      if (response?.deploymentId && response.deploymentId !== deploymentId && typeof setDeploymentSessionId === 'function') {
        setDeploymentSessionId(response.deploymentId);
      }

      if (response && (response.port || response.runnerContainer || response.containerId || response.domain)) {
        handOffToWorkspace(safeName, response);
        return;
      }

      pollUntilDone(pollId, safeName);
    } catch (err) {
      if (!mountedRef.current) return;
      const finishedAt = Date.now();
      setDeployDurationMs(finishedAt - (deployStartedAtRef.current || finishedAt));
      setDeployError(err.message || 'Deployment failed');
      setPhase('error');
      onStatusMessage(`Error: ${err.message}`, 'error');
      setLoading(false);
      openComposeWorkspace(safeName, {
        isDeploying: false,
        deploymentType: 'backend-workspace',
        basePath: '/home/app',
        autoDeploy: true,
        initialTab: 'ai',
        setupError: err.message || 'Automatic setup failed. Use Terminal, Files, or AI to install and run the app.',
        internalPort: internalPort ? parseInt(internalPort, 10) : 3000,
        cpus,
        memory,
        image,
        deployStartedAt: deployStartedAtRef.current,
        onAiVerified: (payload) => finishFromWorkspace(payload)
      });
    }
  };

  if (phase === 'success' && deployResult && workspaceVerified) {
    const rawUrl = deployResult.url || (deployResult.domain ? (deployResult.domain.startsWith('http') ? deployResult.domain : `http://${deployResult.domain}`) : '');
    const displayDomain = deployResult.domain || (rawUrl ? rawUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') : '');

    const labelService = (entry = {}) => {
      if (entry.label) return entry.label;
      const pathHint = entry.path || entry.dir || entry.workDir || entry.role || '';
      const leaf = String(pathHint).split('/').filter(Boolean).pop() || '';
      const role = String(entry.role || '').toLowerCase();
      if (role === 'admin' || role === 'admin-dashboard' || /admin/i.test(leaf)) return 'Admin Dashboard';
      if (role === 'user' || role === 'user-dashboard' || /user/i.test(leaf)) return 'User Dashboard';
      if (role === 'backend' || role === 'api' || role === 'server' || /backend|server|api/i.test(leaf)) return 'Backend API';
      if (role === 'frontend' || /front|client|web/i.test(leaf)) return 'Frontend';
      if (entry.primary) return 'Primary Application';
      if (leaf) return leaf.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      return 'Application';
    };
    const toServiceLink = (entry = {}) => {
      const domain = entry.domain || (entry.url ? String(entry.url).replace(/^https?:\/\//, '').replace(/\/$/, '') : '');
      if (!domain && !entry.url) return null;
      const url = entry.url || (domain ? `http://${domain}/` : null);
      return {
        url,
        domain: domain || url,
        port: entry.port || entry.internalPort || null,
        role: entry.role || null,
        path: entry.path || entry.dir || entry.workDir || null,
        label: labelService(entry),
        primary: Boolean(entry.primary || entry.kind === 'primary')
      };
    };
    const agentBindings = [
      ...(Array.isArray(deployResult?.verification?.bindings) ? deployResult.verification.bindings : []),
      ...(Array.isArray(deployResult?.bindings) ? deployResult.bindings : [])
    ];
    const agentApps = [
      ...(Array.isArray(deployResult?.verification?.apps) ? deployResult.verification.apps : []),
      ...(Array.isArray(deployResult?.apps) ? deployResult.apps : [])
    ];
    const pathKey = (entry = {}) => String(entry.path || entry.dir || entry.workDir || '').replace(/\/+$/, '').toLowerCase();
    const enrichFromBindings = (app) => {
      if (app.domain || app.url) return app;
      const appPath = pathKey(app);
      const appPort = app.port != null ? Number(app.port) : null;
      const appRole = String(app.role || '').toLowerCase();
      const match = agentBindings.find((b) => {
        const bPath = pathKey(b);
        const bPort = b.port != null ? Number(b.port) : null;
        const bRole = String(b.role || '').toLowerCase();
        if (appPort != null && bPort != null && appPort === bPort) return true;
        if (appPath && bPath && (appPath === bPath || appPath.endsWith(bPath) || bPath.endsWith(appPath))) return true;
        if (appRole && bRole && appRole === bRole) return true;
        return false;
      });
      if (!match) return app;
      return {
        ...app,
        domain: match.domain || app.domain,
        url: match.url || app.url,
        port: app.port != null ? app.port : match.port,
        role: app.role || match.role,
        label: app.label || match.label,
        primary: Boolean(app.primary || match.primary || match.kind === 'primary')
      };
    };
    const byDomain = new Map();
    [...agentApps.map(enrichFromBindings), ...agentBindings]
      .map(toServiceLink)
      .filter(Boolean)
      .forEach((link) => {
        const key = String(link.domain || link.url || '').toLowerCase();
        if (!key) return;
        const prev = byDomain.get(key);
        if (!prev || ((link.label && !prev.label) || (link.role && !prev.role) || (link.primary && !prev.primary))) {
          byDomain.set(key, link);
        }
      });
    let serviceLinks = [...byDomain.values()];
    if (!serviceLinks.length && (rawUrl || displayDomain)) {
      const fallback = toServiceLink({
        url: rawUrl,
        domain: displayDomain,
        port: deployResult.port || deployResult.internalPort,
        primary: true,
        role: 'frontend'
      });
      if (fallback) serviceLinks = [fallback];
    }
    const roleRank = (link) => {
      const role = String(link.role || '').toLowerCase();
      const label = String(link.label || '').toLowerCase();
      if (link.primary || role === 'frontend' || label.includes('frontend') || label.includes('primary')) return 1;
      if (role === 'admin' || label.includes('admin')) return 2;
      if (role === 'user' || label.includes('user')) return 3;
      if (role === 'backend' || role === 'api' || label.includes('backend')) return 4;
      return 5;
    };
    serviceLinks.sort((a, b) => roleRank(a) - roleRank(b));

    return (
      <div className="vercel-form-body ai-deploy-page">
        <div className="ai-deploy-top-nav">
          <button
            type="button"
            className="vercel-back-btn-new ai-deploy-back-btn"
            onClick={() => { setPhase('form'); setDeployResult(null); setWorkspaceVerified(false); }}
          >
            <ArrowLeft size={16} />
            <span>Deploy another app</span>
          </button>
          <div className="ai-deploy-top-badge">
            <span className="ai-deploy-pulse-dot" />
            <span>AI Verified & Live</span>
          </div>
        </div>

        <div className="ai-deploy-success">
          <div className="ai-deploy-success-icon-wrapper">
            <div className="ai-deploy-success-icon-pulse" />
            <div className="ai-deploy-success-icon">
              <CheckCircle2 size={36} strokeWidth={2.4} />
            </div>
          </div>

          <h1 className="ai-deploy-success-title">Deployment verified</h1>
          <p className="ai-deploy-success-subtitle">
            <span className="ai-deploy-app-chip">{sanitizeAppName(appName)}</span> is running and verified healthy. Review resource allocation below.
          </p>

          <div className="ai-deploy-elapsed-badge">
            <Clock size={14} />
            <span>Deployed in {formatDeployDuration(deployDurationMs != null ? deployDurationMs : deployElapsedMs)}</span>
          </div>

          {serviceLinks.length > 0 && (
            <div className="ai-deploy-endpoint-card" style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div className="ai-deploy-endpoint-header">
                <div className="ai-deploy-endpoint-label">
                  <span className="ai-deploy-pulse-dot" />
                  <span>Applications & Dashboards</span>
                </div>
                <span className="ai-deploy-protocol-badge">From AI Workspace</span>
              </div>
              {serviceLinks.map((link, i) => (
                <div key={`${link.domain}-${i}`} className="ai-deploy-endpoint-body" style={{ borderTop: i === 0 ? 'none' : '1px solid var(--border)', paddingTop: i === 0 ? 0 : '0.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                    <strong style={{ color: 'var(--text-main)', fontSize: '0.92rem' }}>{link.label}</strong>
                    {link.port != null && (
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontWeight: 600 }}>Port {link.port}</span>
                    )}
                  </div>
                  <a
                    href={link.url || `http://${link.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ai-deploy-endpoint-link"
                  >
                    <Globe size={18} className="ai-deploy-link-icon" />
                    <span>{link.domain}</span>
                  </a>
                </div>
              ))}
            </div>
          )}

          {!serviceLinks.length && (rawUrl || displayDomain) && (
            <div className="ai-deploy-endpoint-card">
              <div className="ai-deploy-endpoint-header">
                <div className="ai-deploy-endpoint-label">
                  <span className="ai-deploy-pulse-dot" />
                  <span>Public Endpoint</span>
                </div>
                <span className="ai-deploy-protocol-badge">Gateway Route</span>
              </div>

              <div className="ai-deploy-endpoint-body">
                <a
                  href={rawUrl || `http://${displayDomain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ai-deploy-endpoint-link"
                >
                  <Globe size={18} className="ai-deploy-link-icon" />
                  <span>{displayDomain || rawUrl}</span>
                </a>

                <div className="ai-deploy-endpoint-actions">
                  <button
                    type="button"
                    className={`ai-deploy-btn-icon-copy ${copiedUrl ? 'copied' : ''}`}
                    onClick={() => handleCopyUrl(rawUrl || `http://${displayDomain}`)}
                    title={copiedUrl ? 'Copied to clipboard' : 'Copy endpoint URL'}
                  >
                    {copiedUrl ? <Check size={15} /> : <Copy size={15} />}
                    <span>{copiedUrl ? 'Copied' : 'Copy URL'}</span>
                  </button>

                  {rawUrl && (
                    <a
                      href={rawUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ai-deploy-btn-open-site"
                    >
                      <span>Open App</span>
                      <ArrowUpRight size={15} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="ai-deploy-details-grid">
            <div className="ai-deploy-detail-card">
              <div className="ai-deploy-card-header">
                <span className="ai-deploy-card-label">Deploy Time</span>
                <Clock size={16} className="ai-deploy-card-icon" />
              </div>
              <div className="ai-deploy-card-value">
                {formatDeployDuration(deployDurationMs != null ? deployDurationMs : deployElapsedMs)}
              </div>
              <div className="ai-deploy-card-sub">AI-verified completion</div>
            </div>

            {deployResult.port != null && (
              <div className="ai-deploy-detail-card">
                <div className="ai-deploy-card-header">
                  <span className="ai-deploy-card-label">Host Port</span>
                  <Radio size={16} className="ai-deploy-card-icon" />
                </div>
                <div className="ai-deploy-card-value mono">{deployResult.port}</div>
                <div className="ai-deploy-card-sub">Public ingress binding</div>
              </div>
            )}

            <div className="ai-deploy-detail-card">
              <div className="ai-deploy-card-header">
                <span className="ai-deploy-card-label">Internal Port</span>
                <Server size={16} className="ai-deploy-card-icon" />
              </div>
              <div className="ai-deploy-card-value mono">{deployResult.internalPort || internalPort || 3000}</div>
              <div className="ai-deploy-card-sub">Container listen target</div>
            </div>

            <div className="ai-deploy-detail-card">
              <div className="ai-deploy-card-header">
                <span className="ai-deploy-card-label">Compute (vCPU)</span>
                <Cpu size={16} className="ai-deploy-card-icon" />
              </div>
              <div className="ai-deploy-card-value">{deployResult.cpus || cpus} Cores</div>
              <div className="ai-deploy-card-sub">Dedicated processor limit</div>
            </div>

            <div className="ai-deploy-detail-card">
              <div className="ai-deploy-card-header">
                <span className="ai-deploy-card-label">Memory (RAM)</span>
                <HardDrive size={16} className="ai-deploy-card-icon" />
              </div>
              <div className="ai-deploy-card-value">{deployResult.memory || memory}</div>
              <div className="ai-deploy-card-sub">Isolated memory allocation</div>
            </div>

            <div className="ai-deploy-detail-card">
              <div className="ai-deploy-card-header">
                <span className="ai-deploy-card-label">Runtime Image</span>
                <Layers size={16} className="ai-deploy-card-icon" />
              </div>
              <div className="ai-deploy-card-value truncate" title={deployResult.image || image}>
                {deployResult.image || image}
              </div>
              <div className="ai-deploy-card-sub">Container base stack</div>
            </div>

            {deployResult.containerName && (
              <div className="ai-deploy-detail-card ai-deploy-detail-card-wide">
                <div className="ai-deploy-card-header">
                  <span className="ai-deploy-card-label">Container Instance</span>
                  <Box size={16} className="ai-deploy-card-icon" />
                </div>
                <div className="ai-deploy-card-value mono">{deployResult.containerName}</div>
                <div className="ai-deploy-card-sub">Active Docker container</div>
              </div>
            )}
          </div>

          <div className="ai-deploy-success-actions">
            {rawUrl && (
              <a
                className="ai-deploy-action-primary"
                href={rawUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Globe size={17} />
                <span>Visit Live App</span>
                <ArrowUpRight size={15} />
              </a>
            )}

            {typeof openComposeWorkspace === 'function' && (
              <button
                type="button"
                className="ai-deploy-secondary-btn"
                onClick={() => openComposeWorkspace(sanitizeAppName(appName), buildWorkspaceInfo(sanitizeAppName(appName), deployResult, {
                  autoDeploy: false,
                  isDeploying: false,
                  onAiVerified: (payload) => finishFromWorkspace(payload)
                }))}
              >
                <Terminal size={17} />
                <span>Reopen Workspace</span>
              </button>
            )}

            <button
              type="button"
              className="ai-deploy-secondary-btn"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('switch-to-dashboard'));
                if (onTabChange) onTabChange('deployments');
              }}
            >
              <LayoutTemplate size={17} />
              <span>Continue to Dashboard</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vercel-form-body ai-deploy-page">
      <button type="button" className="vercel-back-btn-new" onClick={onBack} disabled={loading && phase === 'deploying'}>
        <ArrowLeft size={16} />
        <span>Back to selection</span>
      </button>

      <div className="ai-deploy-hero">
        <div className="ai-deploy-hero-icon">
          <Wand2 size={22} />
        </div>
        <div className="ai-deploy-hero-copy">
          <h1>AI Auto-Deploy</h1>
          <p>
            Configure port, specs, and env, then deploy. The AI workspace opens immediately to install,
            start, and verify your app. Final details appear after verification succeeds.
          </p>
        </div>
      </div>

      {phase === 'error' && deployError && (
        <div className="ai-deploy-error-banner">
          {deployError}
          {(deployDurationMs != null || deployElapsedMs > 0) && (
            <div style={{ marginTop: '0.35rem', fontWeight: 600, opacity: 0.9 }}>
              Failed after {formatDeployDuration(deployDurationMs != null ? deployDurationMs : deployElapsedMs)}
            </div>
          )}
        </div>
      )}

      {(phase === 'deploying' || loading) && (
        <div className="ai-deploy-elapsed-banner">
          <LoaderCircle size={16} className="deploy-spin" />
          <span>Workspace open — AI verifying… {formatDeployDuration(deployElapsedMs)}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="vercel-premium-form ai-deploy-card">
        <section className="ai-deploy-section">
          <div className="ai-deploy-section-head">
            <Archive size={16} />
            <h2>Project ZIP</h2>
          </div>
          <div
            className={`premium-drop-zone ai-deploy-drop${zipFile ? ' active' : ''}${dragOver ? ' drag-over' : ''}`}
            onClick={() => !loading && document.getElementById('aiDeployZipInput')?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              acceptZip(e.dataTransfer.files && e.dataTransfer.files[0]);
            }}
          >
            <Archive size={36} className="drop-icon" />
            <div className="drop-text">
              {zipFile ? <b>{zipFile.name}</b> : 'Drop your project ZIP here, or click to browse'}
            </div>
            {zipFile ? (
              <p className="form-feedback ai-deploy-hint">{Math.round(zipFile.size / 1024)} KB selected</p>
            ) : (
              <p className="form-feedback ai-deploy-hint">.zip only · same path as Git backend deploy</p>
            )}
            <input
              id="aiDeployZipInput"
              type="file"
              accept=".zip,application/zip"
              disabled={loading}
              onChange={(e) => acceptZip(e.target.files && e.target.files[0])}
              style={{ display: 'none' }}
            />
          </div>

          {analyzingZip && (
            <div style={{ marginTop: '1rem', border: '1px solid var(--border)', borderRadius: '6px', background: 'var(--surface-2)', padding: '0.85rem', fontSize: '0.8rem', fontFamily: 'monospace' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', color: 'var(--text-main)', fontWeight: 600 }}>
                <Terminal size={14} /> Analysis Logs
                <LoaderCircle size={12} className="deploy-spin" style={{ marginLeft: 'auto' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', color: 'var(--text-muted)' }}>
                <div>Initializing project upload ({Math.round(zipFile?.size / 1024)} KB)...</div>
                {uploadPct > 0 && <div>[Network] Uploading ZIP archive: {uploadPct}%</div>}
                {uploadPct === 100 && (
                  <>
                    <div>[Network] Upload transfer complete.</div>
                    <div>[Backend] Extracting ZIP contents to secure temp directory...</div>
                    <div>[Worker] Performing recursive structure scan (up to depth 4)...</div>
                    <div>[Worker] Parsing package.json, requirements.txt, and dockerfiles...</div>
                    <div style={{ color: 'var(--primary)', opacity: 0.8 }}>[AI Master] Refining logical detection and assigning internal ports...</div>
                  </>
                )}
              </div>
            </div>
          )}

          {detectedApps.length > 0 && !analyzingZip && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Detected Entry Points</label>
                <button
                  type="button"
                  onClick={() => setEntryPoints([...entryPoints, { path: './', role: 'service', port: 3000, domain: '' }])}
                  disabled={loading}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--primary)',
                    padding: '0.3rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.3rem'
                  }}
                >
                  <Plus size={13} /> Add Entry Point
                </button>
              </div>
              <div style={{ border: '1px solid var(--border)', borderRadius: '0.5rem', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88rem' }}>
                  <thead style={{ background: 'var(--surface-2, rgba(0,0,0,0.03))' }}>
                    <tr>
                      <th style={{ padding: '0.6rem 0.7rem', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Directory</th>
                      <th style={{ padding: '0.6rem 0.7rem', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Role</th>
                      <th style={{ padding: '0.6rem 0.7rem', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Port</th>
                      <th style={{ padding: '0.6rem 0.7rem', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>Subdomain</th>
                      <th style={{ padding: '0.6rem 0.7rem', borderBottom: '1px solid var(--border)', width: '36px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entryPoints.map((ep, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '0.45rem 0.7rem', borderBottom: '1px solid var(--border)' }}>
                          <input
                            type="text"
                            value={ep.path || ''}
                            placeholder="./"
                            disabled={loading}
                            onChange={(e) => {
                              const newEps = [...entryPoints];
                              newEps[idx] = { ...newEps[idx], path: e.target.value };
                              setEntryPoints(newEps);
                            }}
                            style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)', fontSize: '0.85rem', outline: 'none' }}
                          />
                        </td>
                        <td style={{ padding: '0.45rem 0.7rem', borderBottom: '1px solid var(--border)' }}>
                          <input
                            type="text"
                            value={ep.role || ''}
                            placeholder="service"
                            disabled={loading}
                            onChange={(e) => {
                              const newEps = [...entryPoints];
                              newEps[idx] = { ...newEps[idx], role: e.target.value };
                              setEntryPoints(newEps);
                            }}
                            style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)', fontSize: '0.85rem', outline: 'none' }}
                          />
                        </td>
                        <td style={{ padding: '0.45rem 0.7rem', borderBottom: '1px solid var(--border)' }}>
                          <input
                            type="number"
                            value={ep.port || ''}
                            placeholder="3000"
                            disabled={loading}
                            onChange={(e) => {
                              const newEps = [...entryPoints];
                              newEps[idx] = { ...newEps[idx], port: e.target.value === '' ? '' : Number(e.target.value) };
                              setEntryPoints(newEps);
                            }}
                            style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)', fontSize: '0.85rem', outline: 'none' }}
                          />
                        </td>
                        <td style={{ padding: '0.45rem 0.7rem', borderBottom: '1px solid var(--border)' }}>
                          <input
                            type="text"
                            value={ep.domain || ''}
                            placeholder="Auto-assigned"
                            disabled={loading}
                            onChange={(e) => {
                              const newEps = [...entryPoints];
                              newEps[idx] = { ...newEps[idx], domain: e.target.value };
                              setEntryPoints(newEps);
                            }}
                            style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)', fontSize: '0.85rem', outline: 'none' }}
                          />
                        </td>
                        <td style={{ padding: '0.45rem 0.7rem', borderBottom: '1px solid var(--border)' }}>
                          <button
                            type="button"
                            disabled={loading}
                            onClick={() => setEntryPoints(entryPoints.filter((_, i) => i !== idx))}
                            style={{ background: 'transparent', border: 'none', color: 'var(--danger, #dc2626)', cursor: 'pointer', padding: '0.15rem', display: 'flex' }}
                          >
                            <X size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="form-feedback ai-deploy-hint" style={{ marginTop: '0.4rem' }}>
                {entryPoints.length === 1
                  ? 'One entry point detected. Add more if your project has multiple services.'
                  : `${entryPoints.length} entry points detected. Edit ports and subdomains, or add more.`}
              </p>
            </div>
          )}
          {loading && uploadPct > 0 && (
            <div className="ai-deploy-upload-bar">
              <div className="ai-deploy-upload-fill" style={{ width: `${Math.min(100, uploadPct)}%` }} />
              <span>
                {uploadPct < 100
                  ? `Uploading… ${uploadPct}%`
                  : 'Upload complete — provisioning workspace…'}
              </span>
            </div>
          )}
        </section>

        <section className="ai-deploy-section">
          <div className="ai-deploy-section-head">
            <Bot size={16} />
            <h2>Application</h2>
          </div>
          <div className="form-row-grid">
            <div className="form-group">
              <label htmlFor="aiDeployAppName">Application Name <span className="required-star">*</span></label>
              <input
                id="aiDeployAppName"
                type="text"
                placeholder="my-backend-app"
                value={appName}
                onChange={handleAppNameChange}
                disabled={loading}
                required
                style={appNameError ? { borderColor: 'var(--danger, #ef4444)' } : {}}
              />
              {appNameError ? (
                <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                  {appNameError}
                </p>
              ) : (
                <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Use lowercase, numbers, and hyphens only.
                </p>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="aiDeploySubdomain">Platform Subdomain</label>
              <div className={`platform-subdomain-group ${subdomainError ? 'has-error' : ''}`}>
                <input 
                  id="aiDeploySubdomain"
                  type="text" 
                  placeholder={appName ? appName.toLowerCase() : "e.g. my-app"} 
                  value={subdomainPrefix} 
                  onChange={handleSubdomainChange}
                  disabled={loading}
                />
                <span className="platform-subdomain-suffix">
                  {`-${platformSuffix}.devorants.com`}
                </span>
              </div>
              {subdomainError ? (
                <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                  {subdomainError}
                </p>
              ) : (
                <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Preview: <strong style={{ color: 'var(--primary, #6366f1)' }}>{`${(subdomainPrefix || appName || 'app').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-')}-${platformSuffix}.devorants.com`}</strong>
                </p>
              )}
            </div>
          </div>
          <div className="form-row-grid">
            <div className="form-group">
              <label htmlFor="aiDeployDomain">Custom Domain <span className="ai-deploy-optional">optional</span></label>
              <input
                id="aiDeployDomain"
                type="text"
                placeholder="api.example.com"
                value={domain}
                onChange={handleDomainChange}
                disabled={loading}
                style={domainError ? { borderColor: 'var(--danger, #ef4444)' } : {}}
              />
              {domainError ? (
                <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                  {domainError}
                </p>
              ) : (
                <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Your own registered domain. Leave blank to use devorants.com subdomain.
                </p>
              )}
            </div>
          </div>
        </section>

        <section className="ai-deploy-section">
          <div className="ai-deploy-section-head">
            <Server size={16} />
            <h2>Workspace Configuration</h2>
          </div>
          <div className="form-row-grid">
            <div className="form-group">
              <label htmlFor="aiDeployPort">Internal Port <span className="required-star">*</span></label>
              <input
                id="aiDeployPort"
                type="number"
                min={1}
                max={65535}
                value={internalPort}
                onChange={(e) => setInternalPort(e.target.value)}
                disabled={loading}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="aiDeployImage">Runtime Image <span className="required-star">*</span></label>
              <select
                id="aiDeployImage"
                value={image}
                onChange={(e) => setImage(e.target.value)}
                disabled={loading}
              >
                <option value="alma-node">Node.js workspace</option>
                <option value="alma-python">Python workspace</option>
                <option value="alma-linux">Alma Linux workspace</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="aiDeploySpecs">Container Specs (vCPU & RAM) <span className="required-star">*</span></label>
            <select
              id="aiDeploySpecs"
              value={`${cpus}|${memory}`}
              onChange={(e) => {
                const [cpu, mem] = e.target.value.split('|');
                setCpus(cpu);
                setMemory(mem);
              }}
              disabled={loading}
            >
              <option value="0.5|512m">Micro (0.5 vCPU / 512MB RAM)</option>
              <option value="1.0|1024m">Basic (1 vCPU / 1GB RAM)</option>
              <option value="2.0|2048m">Standard (2 vCPU / 2GB RAM)</option>
              <option value="4.0|4096m">Pro (4 vCPU / 4GB RAM)</option>
            </select>
            <p className="form-feedback ai-deploy-hint">Docker CPU and memory limits for this workspace container.</p>
          </div>
        </section>

        <section className="ai-deploy-section">
          <div className="ai-deploy-section-head">
            <Server size={16} />
            <h2>Environment <span className="ai-deploy-optional">optional</span></h2>
          </div>
          <div className="form-group">
            <label htmlFor="aiDeployEnv">ENV variables</label>
            <textarea
              id="aiDeployEnv"
              rows={5}
              placeholder={'KEY=value\nDATABASE_URL=...\nJWT_SECRET=...'}
              value={envText}
              onChange={(e) => setEnvText(e.target.value)}
              disabled={loading}
            />
            <p className="form-feedback ai-deploy-hint">One KEY=value per line. Injected into the workspace container and .env.</p>
          </div>
        </section>

        {loading && workspaceMinimized ? (
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button type="button" onClick={restoreWorkspace} className="premium-deploy-btn ai-deploy-submit" style={{ flex: 1 }}>
              <LayoutTemplate size={20} />
              <span>Open Workspace</span>
            </button>
            <button type="button" onClick={handleCancelDeployment} className="premium-deploy-btn ai-deploy-submit" style={{ flex: 1, background: 'var(--error-surface)', color: 'var(--error)' }}>
              <X size={20} />
              <span>Cancel Deployment</span>
            </button>
          </div>
        ) : loading ? (
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button type="button" disabled className="premium-deploy-btn ai-deploy-submit" style={{ flex: 1 }}>
              <LoaderCircle size={20} className="deploy-spin" />
              <span>{`Deploying… ${formatDeployDuration(deployElapsedMs)}`}</span>
            </button>
            <button type="button" onClick={handleCancelDeployment} className="premium-deploy-btn ai-deploy-submit" style={{ flex: 1, background: 'var(--error-surface)', color: 'var(--error)' }}>
              <X size={20} />
              <span>Cancel Deployment</span>
            </button>
          </div>
        ) : (
          <button type="submit" disabled={loading || Boolean(appNameError) || !appName.trim() || !zipFile} className="premium-deploy-btn ai-deploy-submit" style={{ marginTop: '1rem', ...(appNameError ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }}>
            <Rocket size={20} />
            <span>Open AI Workspace</span>
          </button>
        )}
      </form>
    </div>
  );
}
